import {
  getTiptapCollaborationToken,
  type TiptapCollaborationTokenResponse,
} from "src/services/backend";
import { authHelper } from "src/stores/helpers";

const STORAGE_KEY_PREFIX = "formly:tiptap-collaboration-token:v1";
export const TIPTAP_COLLABORATION_TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

export type CachedTiptapCollaborationToken = TiptapCollaborationTokenResponse & {
  expiresAtMs: number;
};

type TiptapCollaborationTokenOptions = {
  forceRefresh?: boolean;
};

const getStorageKey = (userId: string, orgId: string) =>
  `${STORAGE_KEY_PREFIX}:${userId}:${orgId}`;

const readCachedToken = (
  userId: string,
  orgId: string
): CachedTiptapCollaborationToken | null => {
  const rawCachedToken = localStorage.getItem(getStorageKey(userId, orgId));
  if (!rawCachedToken) {
    return null;
  }

  try {
    const cachedToken = JSON.parse(rawCachedToken);

    if (
      typeof cachedToken?.appId !== "string" ||
      typeof cachedToken?.token !== "string" ||
      typeof cachedToken?.userId !== "string" ||
      typeof cachedToken?.expiresAtMs !== "number" ||
      !Array.isArray(cachedToken?.allowedDocumentNames)
    ) {
      return null;
    }

    return cachedToken;
  } catch {
    return null;
  }
};

const isTokenUsable = (token: CachedTiptapCollaborationToken) =>
  token.expiresAtMs - TIPTAP_COLLABORATION_TOKEN_REFRESH_BUFFER_MS > Date.now();

export const getTiptapCollaborationTokenForOrg = async (
  orgId: string,
  options: TiptapCollaborationTokenOptions = {}
): Promise<CachedTiptapCollaborationToken> => {
  const userId = await authHelper.getUserId();
  const cachedToken = readCachedToken(userId, orgId);

  if (!options.forceRefresh && cachedToken && isTokenUsable(cachedToken)) {
    return cachedToken;
  }

  const response = await getTiptapCollaborationToken({ orgId });
  const nextToken: CachedTiptapCollaborationToken = {
    ...response.data,
    expiresAtMs: Date.now() + response.data.expiresInSeconds * 1000,
  };

  localStorage.setItem(getStorageKey(userId, orgId), JSON.stringify(nextToken));

  return nextToken;
};

export const clearCachedTiptapCollaborationTokens = () => {
  const keysToRemove = [];

  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(STORAGE_KEY_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
};
